import { IThirdPartyAddressModel } from "../services/online-ordering-service.interface";
import {
    IServiceCompleteOrder, IServiceGetCouponCodeById,
    IServiceGetCouponByCode, IServiceGetCouponById,
    IServiceGetGiftCardBalance, IServiceGetLastWaitTimes, IServiceGetOrderNumberId, IServiceGetServerTime,
    IServiceProposedOrder, IServiceSaveOrder, IServiceValidatePromotionCode, IServiceValidatePromotionCodeResult, IServiceGetCouponByCodeOnly,
    IGetOrderItem,
    ICheckAddress
} from "./order-service.interface";
import { Util } from "../utils/util";
import { Order } from "../models/order";
import { IUserRecentOrderDetails } from "../models/user.interface";
import { HttpService } from "../services/http-service"
import { IService } from "./service.interface";
import { Data } from "../models/data";

export class OrderService implements IService {
    private readonly _http: HttpService;
    private readonly _data: Data;
    private readonly _order: Order;
    private readonly _apiOrderUrl: string;
    private readonly _apiOnlineOrderingUrl: string;
    private readonly _publicKey: string;

    constructor(http: HttpService, data: Data, order: Order, apiOrderUrl: string, apiOnlineOrderingUrl: string, publicKey: string) {
        this._http = http;
        this._data = data;
        this._order = order;
        this._apiOrderUrl = apiOrderUrl;
        this._apiOnlineOrderingUrl = apiOnlineOrderingUrl;
        this._publicKey = publicKey;
    }

    get order(): Order {
        return this._order;
    }

    public httpRequestAsync(method: string, url: string, payload: object | null = null, options?: { showProgress?: boolean, noCache?: boolean }): Promise<Response> {
        const storeKey = this._data.getProperty("StoreInfo")?.storeKey
            ?? this._data.getBrandProperty("StoreInfo").storeKey;

        const timeOffset = this._data.getProperty("TimeOffset")
            ?? this._data.getBrandProperty("TimeOffset");

        const businessStartMinute = this._data.getProperty("Settings")?.BSM
            ?? this._data.getBrandProperty("Settings").BSM;

        let businessDate = new Date().toISOString();
        try {
            businessDate = Util.GetBUSDate(Util.NowStore(timeOffset), timeOffset, businessStartMinute).toISOString();
        } catch { }

        return this._http.request(method, url, payload, storeKey, businessDate, { showProgress: options?.showProgress, noCache: options?.noCache });
    }

    public postJSError = async (data: string): Promise<void> => {
        await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}js-error`, { data: data }, { showProgress: false });
    }

    /**
     * Fetches the items associated with the given order ID
     * @async
     * @param {number} orderId - The ID of the order whose items to fetch
     * @return {Promise<string | null>} A Promise that resolves to a JSON string representing the items associated with the given order ID, or null if the request failed or no items were found
     */
    public getOrderItems = async (orderId: number): Promise<IGetOrderItem[]> => {
        const response = await this.httpRequestAsync("GET", `${this._apiOrderUrl}${orderId}`);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? [];
    }

    public getServerTime = async (storeKey: string): Promise<IServiceGetServerTime | null> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}get-server-date`, { SKY: storeKey });
        return response.status === 200 ? await this._http.getJson(response) : null;
    }

    public thirdPartyEstimate = async (data: IThirdPartyAddressModel): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}third-party-estimate`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    public validatePromotionCode = async (data: IServiceValidatePromotionCode): Promise<IServiceValidatePromotionCodeResult> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}validate-promotion-code`, data);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public invalidatePromotionCode = async (data: IServiceValidatePromotionCode): Promise<IServiceValidatePromotionCodeResult> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}invalidate-promotion-code`, data);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public getOrderNumberId = async (data: any): Promise<IServiceGetOrderNumberId> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}get-order-number-id`, data);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false };
    }

    public proposedOrder = async (data: any): Promise<IServiceProposedOrder> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}proposed-order`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public completeOrder = async (data: any): Promise<IServiceCompleteOrder> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}complete-order`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public saveOrder = async (order: string): Promise<IServiceSaveOrder> => {
        const payload = await Util.encrypt(order, this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}save-order`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public saveOrderPayment = async (order: string): Promise<IServiceSaveOrder> => {
        const payload = await Util.encrypt(order, this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}save-order-payment`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public getLastWaitTimes = async (storeKey: string): Promise<IServiceGetLastWaitTimes> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}get-last-wait-times`, { SKY: storeKey });
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, data: "{}" };
    }

    public getGiftCardBalance = async (payload: { SKY: string, CNO: string, PIN: string }): Promise<IServiceGetGiftCardBalance> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}get-gift-card-balance`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, cardNo: "", balance: 0 };
    }

    public getCouponById = async (data: { CID: number, SKY: string }): Promise<IServiceGetCouponById> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}get-coupon-by-id`, data);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, coupon: "", serverError: true };
    }

    public getCouponByCouponCode = async (data: IServiceGetCouponByCodeOnly): Promise<IServiceGetCouponCodeById> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}get-coupon-by-code`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, couponId: null, serverError: true };
    }

    public getCouponByPromotionCode = async (data: IServiceGetCouponByCode): Promise<IServiceGetCouponById> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}get-coupon-by-promotion-code`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, coupon: "", serverError: true };
    }

    public getCouponByCampaignCode = async (data: IServiceGetCouponByCode): Promise<IServiceGetCouponById> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}get-coupon-by-campaign-code`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, coupon: "", serverError: true };
    }

    public checkAddress = async (data: ICheckAddress): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}check-address`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    public getLimitedTimes = async (data: any): Promise<{ success: boolean, data: { min: number, cnt: number }[] }> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}get-limited-times`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, data: [] };
    }

    public reserveLimitedTimes = async (data: any): Promise<{ success: boolean, data: { min: number, cnt: number }[] | null }> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}reserve-limited-times`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, data: null };
    }

    public resetLimitedTimeTimer = async (data: any): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}reset-limited-times-timer`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    public removeOrderLimitedTimes = async (data: any): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}remove-order-limited-times`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    /**
     * Fetches the recent orders of the user, up to 3 orders per page.
     * @async
     * @param {number} pageNumber - the page number to fetch. Starts from 0
     * @returns {Promise<string | null>} a promise that resolves to a json representing the recent orders of user(up to 3), or null if the request failed
     */
    public getRecentOrdersByPage = async (pageNumber: number): Promise<IUserRecentOrderDetails> => {
        const response = await this.httpRequestAsync("GET", `${this._apiOrderUrl}recent-order/${pageNumber}`);
        const json = response.status === 200 ? await this._http.getText(response) : null;
        return json ? JSON.parse(json) : { pagination: 0, recentOrders: [] };
    }
}